import { useMemo } from 'react'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import { styled, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Typography from '@mui/material/Typography'

import Icon from 'src/@core/components/icon/index'

import { stripHtml } from 'string-strip-html'

import { useAuth } from 'src/hooks/useAuth'
import { useTranslationWithKey } from 'src/hooks/useTranslationWithKey'
import { useCustomerEntityLabels } from 'src/hooks/customer/courses/useCustomerEntityLabels'
import { useMultipleInView } from 'src/hooks/courses/useMultipleInView'
import { usePortalSettings } from 'src/hooks/usePortalSettings'
import { useIsFavoriteLessonsEnabled } from 'src/hooks/favorites/useIsFavoriteLessonsEnabled'

import TypeBadge from 'src/components/course-overview/components/TypeBadge'
import { BadgeWrapper } from 'src/components/customer/courses/course-details/ModuleContent'
import { LessonLockReason } from 'src/components/customer/courses/course-details/LessonLockReason'
import { FavoriteButton } from 'src/components/customer/courses/course-details/FavoriteButton'
import { LessonStatusMark } from 'src/components/customer/courses/course-details/LessonStatusMark'

import { getFileUrl } from 'src/utils/getFileUrl'
import { getCoverImageId } from 'src/utils/courses/thumbnailUtils'
import { getEntityNumber } from 'src/utils/courses/customerCoursesUtils'
import { formatDurationFromSeconds } from 'src/utils/date'

import { Lesson, LessonsAndSubmodules, LessonType } from 'src/types/courses/index'

export const LessonImage = styled(Box, {
  shouldForwardProp: prop => prop !== 'bgImage'
})<{ bgImage?: string }>(({ theme, bgImage }) => ({
  display: 'block',
  margin: 'auto',

  aspectRatio: 16 / 9,
  width: '100%',
  borderRadius: theme.spacing(1.5),
  ...(bgImage && {
    backgroundImage: `url(${bgImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  })
}))

type ModuleLessonProps = {
  content: Lesson
  index: number
  lessonsAndSubmodules: LessonsAndSubmodules[]
  handleClickLesson: (lessonId: number) => () => Promise<void>
}

export const ModuleLesson = ({ content, index, handleClickLesson, lessonsAndSubmodules }: ModuleLessonProps) => {
  const theme = useTheme()
  const {
    user,
    currentRoleGetter: { isHiddenCustomer: isPreviewMode }
  } = useAuth()
  const { i18n } = useTranslationWithKey('translation', { keyPrefix: 'PAGE.CUSTOMER_COURSE' })
  const entityLabels = useCustomerEntityLabels()
  const isMDDown = useMediaQuery(theme.breakpoints.down('md'))

  const { portalSettings } = usePortalSettings()
  const showLessonCover = portalSettings?.content_design_settings?.[0]?.show_lesson_cover ?? false

  const { lessonPageDesign, portalDesignImages } = useMemo(
    () => ({
      portalDesignImages: portalSettings?.portal_design_images?.[0],
      lessonPageDesign: portalSettings?.customer_lesson_page_design_settings?.[0]
    }),
    [portalSettings?.customer_lesson_page_design_settings, portalSettings?.portal_design_images]
  )
  const isFavoritesEnabled = useIsFavoriteLessonsEnabled()

  const extractTextFromHTML = (html: string): string => {
    if (!html) return ''

    return stripHtml(html).result.trim()
  }

  const { inViewMap, setRef } = useMultipleInView({ threshold: 0.5 })

  const { is_available } = content

  const isLessonType = content.type_id === LessonType.LESSON
  const imageId = getCoverImageId(content.image_id, isLessonType, portalDesignImages)

  // in preview mode lessons should be possible to be open
  const onClickPreviewLesson = isPreviewMode ? handleClickLesson(content.id) : undefined

  return (
    <Stack key={content?.id}>
      <Stack
        direction='row'
        padding={theme.spacing(2)}
        gap={theme.spacing(4)}
        sx={{
          opacity: !is_available ? 0.5 : 1,
          cursor: !isPreviewMode && !is_available ? 'not-allowed' : 'pointer',
          ':hover': {
            backgroundColor: theme.colorWithOpacity(theme.palette.primary.main, 8),
            transition: 'all 0.1s ease-in-out'
          }
        }}
        onClick={user === null || !is_available ? onClickPreviewLesson : handleClickLesson(content.id)}
      >
        <Stack position='relative' alignItems='center' maxWidth={85} minWidth={85} alignSelf='center' flexShrink={0}>
          {showLessonCover ? (
            <LessonImage
              ref={setRef(index)}
              theme={theme}
              {...(inViewMap[index] && imageId && { bgImage: getFileUrl(`${imageId}`) })}
            />
          ) : (
            <Icon icon={`tabler:${isLessonType ? 'book' : 'pencil'}`} />
          )}

          {isPreviewMode && (
            <BadgeWrapper theme={theme} isSubmodule={false}>
              {!content.is_published && <TypeBadge type={'draft'} />}
            </BadgeWrapper>
          )}
        </Stack>

        <Stack
          sx={{ flex: '1 1 auto', minWidth: 0 }}
          maxWidth={795}
          paddingY={theme.spacing(0.5)}
          justifyContent='center'
        >
          <Typography variant='h6'>{content?.title}</Typography>
          <Typography variant='body2' noWrap title={extractTextFromHTML(content?.payload?.toString() ?? '')}>
            {extractTextFromHTML(content?.payload?.toString() ?? '')}
          </Typography>
          {(lessonPageDesign?.show_entity_name_in_table || lessonPageDesign?.show_numbers_in_table) && (
            <Typography variant='body2' color='text.muted'>
              {lessonPageDesign?.show_entity_name_in_table && entityLabels.getLessonTypeLabel(isLessonType)}
              {lessonPageDesign?.show_entity_name_in_table && lessonPageDesign?.show_numbers_in_table && ' '}
              {lessonPageDesign?.show_numbers_in_table &&
                getEntityNumber(content.id, content.type_id, lessonsAndSubmodules)}
            </Typography>
          )}

          {!!content.content_duration_s && (
            <Stack direction='row' padding={theme.spacing(1, 0, 1, 0)} gap={theme.spacing(1)} alignItems={'center'}>
              <Icon icon='tabler:clock' width={16} />

              <Typography variant='body2' color='text.muted'>
                {formatDurationFromSeconds(content.content_duration_s, i18n.language)}
              </Typography>
            </Stack>
          )}
        </Stack>

        <Stack alignSelf='center' flexShrink={0}>
          <Stack position='relative' direction='row' gap={theme.spacing(4)} alignItems={'center'}>
            {!is_available && <LessonLockReason lockReason={content?.lock_reason} />}

            {is_available && isFavoritesEnabled && (
              <FavoriteButton size={isMDDown ? 'small' : 'medium'} lessonID={content.id} />
            )}

            {is_available && content.lesson_progress?.started_at && (
              <LessonStatusMark
                startedAt={content.lesson_progress?.started_at}
                status={content.lesson_progress?.lesson_progress_id}
              />
            )}
          </Stack>
        </Stack>
      </Stack>

      <Divider sx={{ margin: '0 !important', borderColor: theme.colorWithOpacity(theme.palette.grey[100], 95) }} />
    </Stack>
  )
}
